import { useState, useEffect } from 'react';
import { Button, Table, Spinner, Alert, ButtonGroup, Badge } from 'react-bootstrap';
import { ApiService } from '../services/api';
import { DeploymentStatus } from '../types';
import { ConfirmModal } from './ConfirmModal';
import { WorkloadMetrics } from './WorkloadMetrics';
import { showSuccess, showError } from '../utils/toast';
import { getStatusColor, getStatusIcon, getStatusText, formatTimestamp } from '../utils/reporting';

interface ConfigListProps {
  onEdit: (workloadName: string) => void;
  onCreate: () => void;
}

export function ConfigList({ onEdit, onCreate }: ConfigListProps) {
  const [configs, setConfigs] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deploymentStatus, setDeploymentStatus] = useState<DeploymentStatus | null>(null);
  const [selectedMetrics, setSelectedMetrics] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ show: boolean; workloadName: string | null }>({
    show: false,
    workloadName: null
  });

  useEffect(() => {
    loadConfigs();
    loadDeploymentStatus();
    // Refresh deployment status every 2 minutes
    const interval = setInterval(loadDeploymentStatus, 120000);
    return () => clearInterval(interval);
  }, []);

  const loadConfigs = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await ApiService.listConfigs();
      setConfigs(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load configurations');
    } finally {
      setLoading(false);
    }
  };

  const loadDeploymentStatus = async () => {
    try {
      const status = await ApiService.getDeploymentStatus();
      setDeploymentStatus(status);
    } catch (err) {
      console.error('Failed to load deployment status:', err);
      // Don't show error to user, just log it
    }
  };

  const getWorkloadStatus = (workloadName: string) => {
    return deploymentStatus?.workloads.find(w => w.name === workloadName);
  };

  const handleDelete = (workloadName: string) => {
    setDeleteConfirm({ show: true, workloadName });
  };

  const confirmDelete = async () => {
    if (!deleteConfirm.workloadName) return;

    try {
      await ApiService.deleteConfig(deleteConfirm.workloadName);
      showSuccess(`Workload ${deleteConfirm.workloadName} deleted successfully`);
      await loadConfigs();
    } catch (err) {
      showError(
        err instanceof Error ? err.message : 'Unknown error',
        'Failed to Delete'
      );
    } finally {
      setDeleteConfirm({ show: false, workloadName: null });
    }
  };

  const cancelDelete = () => {
    setDeleteConfirm({ show: false, workloadName: null });
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <p className="mt-3">Loading configurations...</p>
      </div>
    );
  }

  if (error) {
    return (
      <>
        <Alert variant="danger">
          <Alert.Heading>Error</Alert.Heading>
          <p>{error}</p>
          <Button variant="outline-danger" onClick={loadConfigs}>
            Retry
        </Button>
      </Alert>
      </>
    );
  }

  return (
    <>
    <div className="card">
      <div className="card-header">
        <h3 className="card-title">
          <i className="fas fa-cogs mr-2"></i>
          Workload Configurations
        </h3>
        <div className="card-tools">
          <ButtonGroup size="sm">
            <Button variant="primary" onClick={onCreate}>
              <i className="fas fa-plus-circle mr-2"></i>
              Create New
            </Button>
          </ButtonGroup>
        </div>
      </div>

      <div className="card-body">
        {configs.length === 0 ? (
          <div className="text-center py-5">
            <p className="text-muted mb-3">No configurations found</p>
            <Button variant="primary" onClick={onCreate}>
              Create your first configuration
            </Button>
          </div>
        ) : (
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>Workload Name</th>
                <th style={{ width: '150px' }}>Status</th>
                <th style={{ width: '100px' }}>Last Updated</th>
                <th style={{ width: '200px' }}>Account</th>
                <th style={{ width: '100px' }}>LZA MR</th>
                <th style={{ width: '280px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {configs.map((name) => {
                const status = getWorkloadStatus(name);
                return (
                  <tr key={name}>
                    <td className="align-middle">
                      <strong>{name}</strong>
                    </td>
                    <td className="align-middle">
                      {status ? (
                        <Badge bg={getStatusColor(status.status)}>
                          {getStatusIcon(status.status)} {getStatusText(status.status)}
                        </Badge>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td className="align-middle">
                      {status?.lastDeployed ? (
                        <small title={new Date(status.lastDeployed).toLocaleString()}>
                          {formatTimestamp(status.lastDeployed)}
                        </small>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td className="align-middle">
                      {status?.type === 'account-vending' && status.accountEmail ? (
                        <small className="text-muted font-monospace">{status.accountEmail}</small>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td className="align-middle text-center">
                      {status?.type === 'account-vending' && status.lzaMergeRequestUrl ? (
                        <a 
                          href={status.lzaMergeRequestUrl} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="btn btn-sm btn-outline-secondary"
                          title="View LZA Merge Request"
                        >
                          <i className="fas fa-external-link-alt"></i>
                        </a>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td>
                      <ButtonGroup size="sm">
                        <Button 
                          variant="outline-info" 
                          onClick={() => setSelectedMetrics(name)}
                          title="View deployment metrics"
                        >
                          <i className="fas fa-chart-line me-1"></i>
                          Metrics
                        </Button>
                        <Button variant="outline-primary" onClick={() => onEdit(name)}>
                          <i className="fas fa-edit me-1"></i>
                          Edit
                        </Button>
                        <Button variant="outline-danger" onClick={() => handleDelete(name)}>
                          <i className="fas fa-trash me-1"></i>
                          Delete
                        </Button>
                      </ButtonGroup>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
      </div>
    </div>

    <ConfirmModal
      show={deleteConfirm.show}
      title="Delete Workload"
      message={`Are you sure you want to delete ${deleteConfirm.workloadName}? This will create a merge request to remove the configuration.`}
      confirmText="Delete"
      cancelText="Cancel"
      variant="danger"
      onConfirm={confirmDelete}
      onCancel={cancelDelete}
    />

    {selectedMetrics && (
      <WorkloadMetrics 
        workloadName={selectedMetrics}
        onClose={() => setSelectedMetrics(null)}
      />
    )}
  </>
  );
}
